import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { getCompanyTokenQuota, pushCompanyTokenQuota, updateCompanyTokenQuota } from '@/store/chat/actions';

import {
  selectTokenQuota,
  selectTokenQuotaPercentage,
  selectTokenUsageErrors,
  selectTokenUsageLoading,
} from '@/store/chat/selectors';
import { selectUsers } from '@/store/users/selectors';

import { Progress, Tooltip } from 'antd';
import { FiInfo } from 'react-icons/fi';

import TokenUsageModal from './TokenUsageModal';

import Utils from '@/Utils';

import styles from './TokenUsageIndicator.module.scss';

const TokenUsageIndicator = ({ socket }) => {
  const [modalVisible, setModalVisible] = useState(false);
  const { t } = useTranslation('chat');
  const dispatch = useDispatch();
  const tokenQuota = useSelector(selectTokenQuota);
  const percentage = useSelector(selectTokenQuotaPercentage);
  const loading = useSelector(selectTokenUsageLoading);
  const errors = useSelector(selectTokenUsageErrors);

  const { currentUser } = useSelector(selectUsers);
  const isAdmin = Utils.isAdminOrSuperAdmin(currentUser);

  // Загружаем данные о токенах при монтировании компонента
  useEffect(() => {
    dispatch(getCompanyTokenQuota());
    dispatch(pushCompanyTokenQuota()); // Подписываемся на обновления через сокет
  }, [dispatch]);

  // Обработка сокет-событий для лимитов токенов
  useEffect(() => {
    if (!socket) return;

    const handleTokenQuotaUpdate = quotaData => {
      console.log('📊 Received token quota update:', quotaData);
      dispatch(updateCompanyTokenQuota(quotaData));
    };

    socket.on('companyTokenQuota', handleTokenQuotaUpdate);

    return () => {
      socket.off('companyTokenQuota', handleTokenQuotaUpdate);
    };
  }, [socket, dispatch]);

  const handleClick = useCallback(() => {
    setModalVisible(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setModalVisible(false);
  }, []);

  // Определяем цвет прогресс-бара в зависимости от процента использования
  const getProgressColor = percent => {
    if (percent >= 90) return '#ff4d4f'; // Красный
    if (percent >= 75) return '#faad14'; // Оранжевый
    return '#52c41a'; // Зеленый
  };
  // Форматируем числа для отображения
  const formatTokenCount = count => {
    const numericCount = parseInt(count) || 0;
    if (numericCount >= 1000000) {
      return `${(numericCount / 1000000).toFixed(1)}M`;
    }
    if (numericCount >= 1000) {
      return `${(numericCount / 1000).toFixed(1)}K`;
    }
    return numericCount.toString();
  };

  if (loading.getCompanyTokenQuota) {
    return (
      <div className={styles.tokenIndicator}>
        <div className={styles.loading}>
          <div className={styles.spinner} />
        </div>
      </div>
    );
  }

  if (errors.getCompanyTokenQuota || !tokenQuota.global_limit) {
    return null; // Не показываем компонент если нет данных или ошибка
  }

  return (
    <>
      <div className={styles.tokenIndicator} onClick={isAdmin && handleClick}>
        <Tooltip
          title={`${t('tokenUsage.used')}: ${formatTokenCount(tokenQuota.global_used)} / ${formatTokenCount(tokenQuota.global_limit)} (${percentage}%)`}
          placement='bottom'
        >
          <div className={styles.progressContainer}>
            <Progress
              type='circle'
              size={24}
              percent={percentage}
              strokeColor={getProgressColor(percentage)}
              showInfo={false}
              strokeWidth={8}
            />
            <span className={styles.percentage}>{percentage}%</span>
          </div>
        </Tooltip>
        <FiInfo className={styles.infoIcon} />
      </div>

      <TokenUsageModal visible={modalVisible} onClose={handleModalClose} tokenQuota={tokenQuota} />
    </>
  );
};

export default TokenUsageIndicator;
